import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Loader2, CheckCircle2, XCircle, RefreshCw, Clock,
  ClipboardCheck, ShieldCheck, AlertTriangle, Camera,
  ChevronRight, ChevronDown, Zap, BarChart3, TrendingUp,
  TrendingDown, X, RotateCcw, Trash2, Upload, Search,
  Filter, ArrowRight, FileText, ChevronUp, Minus,
} from 'lucide-react';
import { api } from '@/utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ManufacturingOrder {
  id: string;
  orderNumber: string;
  productName: string;
  targetQty: number;
  currentProcess: number;
  status: string;
  isSWH: boolean;
  priority: string;
  barcodeEntry?: string | null;
  updatedAt: string;
  createdAt: string;
  poNumber?: string;
}

interface ChecklistItem {
  id: string;
  name: string;
  required: boolean;
}

interface QCParameter {
  id: string;
  name: string;
  unit: string;
  min: number;
  max: number;
  critical: boolean;
}

interface QCTemplate {
  name: string;
  checklist: ChecklistItem[];
  parameters: QCParameter[];
}

type QCTypeFilter = 'all' | 'rm' | 'sfg' | 'fg';

interface ChecklistState {
  [itemId: string]: { passed: boolean | null; notes: string };
}

interface ParameterState {
  [paramId: string]: { value: string };
}

type ParameterValidation = 'in-range' | 'near-boundary' | 'out-of-range' | 'empty';

// ── QC Templates ──────────────────────────────────────────────────────────────

const QC_TEMPLATES: Record<number, QCTemplate> = {
  0: {
    name: 'RM Incoming Inspection',
    checklist: [
      { id: 'rm1', name: 'Grade verification (match PO spec)', required: true },
      { id: 'rm2', name: 'Thickness measurement (within tolerance)', required: true },
      { id: 'rm3', name: 'Quantity received vs PO quantity', required: true },
      { id: 'rm4', name: 'Visual inspection (surface defects, rust, damage)', required: true },
      { id: 'rm5', name: 'Certificate of conformity received', required: true },
      { id: 'rm6', name: 'Storage area designated', required: false },
    ],
    parameters: [
      { id: 'rmp1', name: 'Sheet Thickness', unit: 'mm', min: 1.95, max: 2.05, critical: true },
      { id: 'rmp2', name: 'Surface Roughness', unit: 'Ra', min: 0, max: 1.6, critical: false },
    ],
  },
  5: {
    name: 'Leakage / Pressure Test',
    checklist: [
      { id: 'lt1', name: 'Tank filled with water', required: true },
      { id: 'lt2', name: 'Pressure applied per spec', required: true },
      { id: 'lt3', name: 'Held for 30 minutes', required: true },
      { id: 'lt4', name: 'Visual inspection for leaks at all joints', required: true },
      { id: 'lt5', name: 'Pressure gauge reading stable', required: true },
    ],
    parameters: [
      { id: 'ltp1', name: 'Hold Time', unit: 'min', min: 30, max: 30, critical: true },
      { id: 'ltp2', name: 'Pressure', unit: 'bar', min: 3, max: 5, critical: true },
    ],
  },
  10: {
    name: 'QC-1: PUF Quality Check',
    checklist: [
      { id: 'puf1', name: 'Visual inspection of inner tank + cladding', required: true },
      { id: 'puf2', name: 'Release agent / cleanliness check', required: true },
      { id: 'puf3', name: 'Catalyst mixing verified', required: true },
      { id: 'puf4', name: 'Machine pressure within spec', required: true },
      { id: 'puf5', name: 'Machine temperature within spec', required: true },
      { id: 'puf6', name: 'Trial shot / density sample taken', required: true },
      { id: 'puf7', name: 'PUF filling / injection completed', required: true },
      { id: 'puf8', name: 'Foaming rise observation', required: true },
      { id: 'puf9', name: 'Curing time verified', required: true },
      { id: 'puf10', name: 'Gap fill check', required: true },
      { id: 'puf11', name: 'Surface bulging / shrinkage check', required: true },
      { id: 'puf12', name: 'Leakage around joints check', required: true },
      { id: 'puf13', name: 'Chemical consumption recorded', required: true },
    ],
    parameters: [
      { id: 'pufp1', name: 'Machine Pressure', unit: 'bar', min: 125, max: 135, critical: true },
      { id: 'pufp2', name: 'Machine Temperature', unit: '\u00B0C', min: 19, max: 25, critical: true },
      { id: 'pufp3', name: 'Foam Density', unit: 'kg/m\u00B3', min: 38.8, max: 41.2, critical: true },
      { id: 'pufp4', name: 'Chemical Consumption Variance', unit: '%', min: 0, max: 3, critical: true },
      { id: 'pufp5', name: 'Curing Time', unit: 'min', min: 25, max: 35, critical: false },
    ],
  },
  13: {
    name: 'QC-2: Packing & Labeling',
    checklist: [
      { id: 'pk1', name: 'In-process cleaning supervision', required: true },
      { id: 'pk2', name: 'Cleaning quality inspection passed', required: true },
      { id: 'pk3', name: 'Packing as per SOP', required: true },
      { id: 'pk4', name: 'Label & marking verification', required: true },
      { id: 'pk5', name: 'Post-packing inspection (no scratch/dent)', required: true },
      { id: 'pk6', name: 'Barcode entry in ERP', required: true },
      { id: 'pk7', name: 'Packing qty & material usage recorded', required: true },
    ],
    parameters: [
      { id: 'pkp1', name: 'Packing Material Usage Variance', unit: '%', min: 0, max: 3, critical: true },
    ],
  },
  14: {
    name: 'QC-3: Final Inspection',
    checklist: [
      { id: 'fi1', name: 'Functional test (hot water circulation)', required: true },
      { id: 'fi2', name: 'Insulation integrity check', required: true },
      { id: 'fi3', name: 'Fittings & connections tightness', required: true },
      { id: 'fi4', name: 'Cosmetic finish inspection', required: true },
      { id: 'fi5', name: 'BIS/ISO standards compliance', required: true },
      { id: 'fi6', name: 'Warranty sticker applied', required: true },
      { id: 'fi7', name: 'Final barcode verification against SO', required: true },
      { id: 'fi8', name: 'Inspector details recorded', required: true },
    ],
    parameters: [],
  },
  15: {
    name: 'QC-3 Frame: SWH Frame Packing',
    checklist: [
      { id: 'fr1', name: 'Visual verification (model, size, weld finish)', required: true },
      { id: 'fr2', name: 'Surface finish check (coating thickness, scratches)', required: true },
      { id: 'fr3', name: 'Frame matching as per BOM', required: true },
      { id: 'fr4', name: 'Count verification (members qty vs standard)', required: true },
      { id: 'fr5', name: 'Protection material check', required: true },
      { id: 'fr6', name: 'Label & identification check', required: true },
      { id: 'fr7', name: 'Sharp edge protection', required: true },
      { id: 'fr8', name: 'Corner protection', required: true },
      { id: 'fr9', name: 'Strap tightness', required: true },
      { id: 'fr10', name: 'Polywrap sealing', required: true },
      { id: 'fr11', name: 'Accessory pouch fixed', required: true },
    ],
    parameters: [],
  },
};

// QC stage IDs mapped to types
const QC_RM_STAGES = [0];
const QC_SFG_STAGES = [5, 10];
const QC_FG_STAGES = [13, 14, 15];
const ALL_QC_STAGES = [...QC_RM_STAGES, ...QC_SFG_STAGES, ...QC_FG_STAGES];

function getQCType(stage: number): 'RM' | 'SFG' | 'FG' {
  if (QC_RM_STAGES.includes(stage)) return 'RM';
  if (QC_SFG_STAGES.includes(stage)) return 'SFG';
  return 'FG';
}

function getQCTypeBadgeClasses(type: 'RM' | 'SFG' | 'FG'): string {
  switch (type) {
    case 'RM': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'SFG': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'FG': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  }
}

function getQCTypeDotColor(type: 'RM' | 'SFG' | 'FG'): string {
  switch (type) {
    case 'RM': return 'bg-purple-500';
    case 'SFG': return 'bg-blue-500';
    case 'FG': return 'bg-emerald-500';
  }
}

const PRIORITY_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  CRITICAL: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' },
  HIGH: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
  NORMAL: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Normal' },
  LOW: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Low' },
};

function getTimeWaiting(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function validateParameter(value: string, min: number, max: number): ParameterValidation {
  if (!value || value.trim() === '') return 'empty';
  const num = parseFloat(value);
  if (isNaN(num)) return 'out-of-range';
  if (num < min || num > max) return 'out-of-range';
  const range = max - min;
  const threshold = range * 0.1;
  if (range > 0 && (num - min < threshold || max - num < threshold)) return 'near-boundary';
  return 'in-range';
}

function getValidationClasses(validation: ParameterValidation): string {
  switch (validation) {
    case 'in-range': return 'bg-emerald-50 border-emerald-300 text-emerald-800';
    case 'near-boundary': return 'bg-amber-50 border-amber-300 text-amber-800';
    case 'out-of-range': return 'bg-red-50 border-red-300 text-red-800';
    default: return 'bg-white border-gray-300 text-gray-900';
  }
}

function getValidationIcon(validation: ParameterValidation) {
  switch (validation) {
    case 'in-range': return <CheckCircle2 size={16} className="text-emerald-600" />;
    case 'near-boundary': return <AlertTriangle size={16} className="text-amber-600" />;
    case 'out-of-range': return <XCircle size={16} className="text-red-600" />;
    default: return <Minus size={16} className="text-gray-300" />;
  }
}

// ── Rejection Reasons ─────────────────────────────────────────────────────────

const REJECTION_REASONS = [
  'Material defect',
  'Dimensional out of spec',
  'Surface damage / scratch',
  'Leakage detected',
  'PUF density failure',
  'Wrong component / grade',
  'Welding defect',
  'Assembly error',
  'Coating defect',
  'Missing documentation',
  'Packaging damage',
  'Functional test failure',
  'Other',
];

// ── Mock Statistics (will be API-driven) ──────────────────────────────────────

const MOCK_STATS = {
  topRejections: [
    { reason: 'PUF density failure', count: 5 },
    { reason: 'Surface damage / scratch', count: 3 },
    { reason: 'Leakage detected', count: 2 },
    { reason: 'Dimensional out of spec', count: 2 },
    { reason: 'Welding defect', count: 1 },
  ],
  passRateThisWeek: 87,
  passRateLastWeek: 82,
};

// ── Main Component ────────────────────────────────────────────────────────────

export function QCManagementPage() {
  const [orders, setOrders] = useState<ManufacturingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<QCTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<ManufacturingOrder | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(true);

  // QC form state
  const [checklistState, setChecklistState] = useState<ChecklistState>({});
  const [parameterState, setParameterState] = useState<ParameterState>({});
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [showFailModal, setShowFailModal] = useState<'rework' | 'reject' | null>(null);
  const [failReason, setFailReason] = useState('');
  const [failNotes, setFailNotes] = useState('');
  const [showPassConfirm, setShowPassConfirm] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch orders ──────────────────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/manufacturing-flow/orders');
      const allOrders: ManufacturingOrder[] = Array.isArray(data) ? data : data.orders || data.data || [];
      // Filter to only QC stages
      const qcOrders = allOrders.filter((o) => ALL_QC_STAGES.includes(o.currentProcess));
      setOrders(qcOrders);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ── Filter orders by tab + search ─────────────────────────────────────────

  const filteredOrders = useMemo(() => {
    let result = orders;

    if (activeTab !== 'all') {
      const stageSet =
        activeTab === 'rm' ? QC_RM_STAGES :
        activeTab === 'sfg' ? QC_SFG_STAGES :
        QC_FG_STAGES;
      result = result.filter((o) => stageSet.includes(o.currentProcess));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(q) ||
          o.productName.toLowerCase().includes(q) ||
          (o.poNumber && o.poNumber.toLowerCase().includes(q))
      );
    }

    // Sort: CRITICAL first, then HIGH, then by updatedAt desc
    const priorityOrder: Record<string, number> = { CRITICAL: 0, HIGH: 1, NORMAL: 2, LOW: 3 };
    return result.sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 2;
      const pb = priorityOrder[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [orders, activeTab, searchQuery]);

  // ── Statistics ────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const pending = orders.length;
    // Mock passed/failed today — in production these come from API
    const passedToday = Math.floor(Math.random() * 5) + 8;
    const failedToday = Math.floor(Math.random() * 3) + 1;
    const passRate = passedToday + failedToday > 0
      ? Math.round((passedToday / (passedToday + failedToday)) * 100)
      : 0;
    return { pending, passedToday, failedToday, passRate };
  }, [orders]);

  // ── Open QC Panel ─────────────────────────────────────────────────────────

  const openInspection = useCallback((order: ManufacturingOrder) => {
    setSelectedOrder(order);
    const template = QC_TEMPLATES[order.currentProcess];
    if (template) {
      const cls: ChecklistState = {};
      template.checklist.forEach((item) => {
        cls[item.id] = { passed: null, notes: '' };
      });
      setChecklistState(cls);

      const ps: ParameterState = {};
      template.parameters.forEach((p) => {
        ps[p.id] = { value: '' };
      });
      setParameterState(ps);
    }
    setExpandedNotes(new Set());
    setUploadedPhotos([]);
    setShowFailModal(null);
    setShowPassConfirm(false);
    setFailReason('');
    setFailNotes('');
    setPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setTimeout(() => setSelectedOrder(null), 300);
  }, []);

  // ── Checklist handlers ────────────────────────────────────────────────────

  const toggleChecklist = useCallback((itemId: string, passed: boolean) => {
    setChecklistState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], passed: prev[itemId]?.passed === passed ? null : passed },
    }));
  }, []);

  const updateChecklistNotes = useCallback((itemId: string, notes: string) => {
    setChecklistState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes },
    }));
  }, []);

  const toggleNotes = useCallback((itemId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  // ── Parameter handlers ────────────────────────────────────────────────────

  const updateParameter = useCallback((paramId: string, value: string) => {
    setParameterState((prev) => ({
      ...prev,
      [paramId]: { value },
    }));
  }, []);

  // ── Photo upload (mock) ───────────────────────────────────────────────────

  const handlePhotoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedPhotos((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removePhoto = useCallback((index: number) => {
    setUploadedPhotos((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Pass/Fail validation ──────────────────────────────────────────────────

  const currentTemplate = selectedOrder ? QC_TEMPLATES[selectedOrder.currentProcess] : null;

  const { canPass, allRequiredChecked, noCriticalFails } = useMemo(() => {
    if (!currentTemplate) return { canPass: false, allRequiredChecked: false, noCriticalFails: true };

    const allReq = currentTemplate.checklist
      .filter((c) => c.required)
      .every((c) => checklistState[c.id]?.passed === true);

    const noCrit = currentTemplate.parameters
      .filter((p) => p.critical)
      .every((p) => {
        const val = parameterState[p.id]?.value;
        if (!val || val.trim() === '') return false;
        const v = validateParameter(val, p.min, p.max);
        return v !== 'out-of-range';
      });

    return { canPass: allReq && noCrit, allRequiredChecked: allReq, noCriticalFails: noCrit };
  }, [currentTemplate, checklistState, parameterState]);

  // ── Submit handlers ───────────────────────────────────────────────────────

  const handlePass = useCallback(async () => {
    if (!selectedOrder) return;
    try {
      setSubmitting(true);
      await api.post(`/manufacturing-flow/orders/${selectedOrder.id}/qc/${selectedOrder.currentProcess}/pass`, {
        checklist: checklistState,
        parameters: parameterState,
        photos: uploadedPhotos.length,
      });
      await api.patch(`/manufacturing-flow/orders/${selectedOrder.id}/advance`);
      setShowPassConfirm(false);
      closePanel();
      fetchOrders();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to submit QC pass');
    } finally {
      setSubmitting(false);
    }
  }, [selectedOrder, checklistState, parameterState, uploadedPhotos, closePanel, fetchOrders]);

  const handleFail = useCallback(async (type: 'rework' | 'reject') => {
    if (!selectedOrder || !failReason) return;
    try {
      setSubmitting(true);
      await api.post(`/manufacturing-flow/orders/${selectedOrder.id}/qc/${selectedOrder.currentProcess}/fail`, {
        type,
        reason: failReason,
        notes: failNotes,
        checklist: checklistState,
        parameters: parameterState,
      });
      setShowFailModal(null);
      closePanel();
      fetchOrders();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to submit QC fail');
    } finally {
      setSubmitting(false);
    }
  }, [selectedOrder, failReason, failNotes, checklistState, parameterState, closePanel, fetchOrders]);

  // ── Tab Counts ────────────────────────────────────────────────────────────

  const tabCounts = useMemo(() => ({
    all: orders.length,
    rm: orders.filter((o) => QC_RM_STAGES.includes(o.currentProcess)).length,
    sfg: orders.filter((o) => QC_SFG_STAGES.includes(o.currentProcess)).length,
    fg: orders.filter((o) => QC_FG_STAGES.includes(o.currentProcess)).length,
  }), [orders]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-blue-600" size={36} />
          <p className="text-sm text-gray-500">Loading QC inspections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                <ShieldCheck size={22} className="text-white" />
              </div>
              QC Management
            </h1>
            <p className="text-sm text-gray-500 mt-1 ml-[52px]">
              Unified quality checkpoint dashboard for all manufacturing stages
            </p>
          </div>
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-6 py-6 max-w-[1600px] mx-auto">
        {/* ── Error ───────────────────────────────────────────────────────── */}
        {error && (
          <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} className="shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1.5 mb-6 w-fit shadow-sm">
          {([
            { key: 'all', label: 'All' },
            { key: 'rm', label: 'RM Inspection' },
            { key: 'sfg', label: 'SFG Inspection' },
            { key: 'fg', label: 'FG Inspection' },
          ] as { key: QCTypeFilter; label: string }[]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-blue-500 text-blue-100' : 'bg-gray-200 text-gray-600'
              }`}>
                {tabCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* ── Stats Row ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Pending Inspections"
            value={stats.pending}
            icon={<ClipboardCheck size={20} />}
            color="amber"
          />
          <StatCard
            label="Passed Today"
            value={stats.passedToday}
            icon={<CheckCircle2 size={20} />}
            color="green"
          />
          <StatCard
            label="Failed Today"
            value={stats.failedToday}
            icon={<XCircle size={20} />}
            color="red"
          />
          <StatCard
            label="Pass Rate"
            value={`${stats.passRate}%`}
            icon={<BarChart3 size={20} />}
            color="blue"
          />
        </div>

        {/* ── Search ──────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order #, product, or PO..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter size={14} />
            {filteredOrders.length} inspection{filteredOrders.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* ── Main Content Grid ───────────────────────────────────────────── */}
        <div className="flex gap-6">
          {/* Inspections List */}
          <div className="flex-1 min-w-0">
            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4">
                  <CheckCircle2 size={28} className="text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">All clear!</h3>
                <p className="text-sm text-gray-500">
                  No pending QC inspections{activeTab !== 'all' ? ` for ${activeTab.toUpperCase()} stage` : ''}.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order) => {
                  const qcType = getQCType(order.currentProcess);
                  const template = QC_TEMPLATES[order.currentProcess];
                  const priorityCfg = PRIORITY_CONFIG[order.priority] || PRIORITY_CONFIG.NORMAL;
                  const timeStr = getTimeWaiting(order.updatedAt);

                  return (
                    <div
                      key={order.id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200 group"
                    >
                      <div className="p-5 flex items-center gap-5">
                        {/* Type indicator */}
                        <div className={`w-1.5 h-16 rounded-full shrink-0 ${getQCTypeDotColor(qcType)}`} />

                        {/* Main info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="font-semibold text-gray-900 text-base">
                              {order.orderNumber}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border ${getQCTypeBadgeClasses(qcType)}`}>
                              {qcType}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ${priorityCfg.bg} ${priorityCfg.text}`}>
                              {priorityCfg.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 font-medium truncate">
                            {order.productName}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {template?.name || `Stage ${order.currentProcess}`}
                          </p>
                        </div>

                        {/* Time waiting */}
                        <div className="flex items-center gap-1.5 text-sm text-gray-400 shrink-0">
                          <Clock size={14} />
                          <span className="font-medium">{timeStr}</span>
                        </div>

                        {/* Checklist count badge */}
                        <div className="text-xs text-gray-400 shrink-0 text-center">
                          <span className="block font-semibold text-gray-600 text-lg leading-none">
                            {template?.checklist.length || 0}
                          </span>
                          checks
                        </div>

                        {/* Action */}
                        <button
                          onClick={() => openInspection(order)}
                          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm group-hover:shadow-md shrink-0"
                        >
                          Start Inspection
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Right Statistics Panel ─────────────────────────────────────── */}
          <div className="w-80 shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden sticky top-6">
              <button
                onClick={() => setStatsExpanded(!statsExpanded)}
                className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 size={16} className="text-blue-600" />
                  QC Statistics
                </h3>
                {statsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {statsExpanded && (
                <div className="p-5 space-y-6">
                  {/* Pass Rate Donut */}
                  <div className="text-center">
                    <div className="relative w-32 h-32 mx-auto mb-3">
                      <svg viewBox="0 0 100 100" className="transform -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#f3f4f6" strokeWidth="10" />
                        <circle
                          cx="50" cy="50" r="40" fill="none"
                          stroke="#10b981" strokeWidth="10"
                          strokeDasharray={`${MOCK_STATS.passRateThisWeek * 2.51} 251`}
                          strokeLinecap="round"
                          className="transition-all duration-1000"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-gray-900">{MOCK_STATS.passRateThisWeek}%</span>
                        <span className="text-xs text-gray-500">pass rate</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-1.5 text-sm">
                      {MOCK_STATS.passRateThisWeek >= MOCK_STATS.passRateLastWeek ? (
                        <>
                          <TrendingUp size={14} className="text-emerald-600" />
                          <span className="text-emerald-600 font-medium">
                            +{MOCK_STATS.passRateThisWeek - MOCK_STATS.passRateLastWeek}%
                          </span>
                        </>
                      ) : (
                        <>
                          <TrendingDown size={14} className="text-red-600" />
                          <span className="text-red-600 font-medium">
                            {MOCK_STATS.passRateThisWeek - MOCK_STATS.passRateLastWeek}%
                          </span>
                        </>
                      )}
                      <span className="text-gray-400">vs last week</span>
                    </div>
                  </div>

                  {/* Top Rejection Reasons */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Top Rejection Reasons
                    </h4>
                    <div className="space-y-2.5">
                      {MOCK_STATS.topRejections.map((r, i) => {
                        const maxCount = MOCK_STATS.topRejections[0].count;
                        const pct = maxCount > 0 ? (r.count / maxCount) * 100 : 0;
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-700 truncate pr-2">{r.reason}</span>
                              <span className="font-semibold text-gray-900 shrink-0">{r.count}</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-red-400 rounded-full transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick Summary */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <span className="block text-xl font-bold text-gray-900">{tabCounts.rm}</span>
                        <span className="text-xs text-purple-600 font-medium">RM Pending</span>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <span className="block text-xl font-bold text-gray-900">{tabCounts.sfg}</span>
                        <span className="text-xs text-blue-600 font-medium">SFG Pending</span>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <span className="block text-xl font-bold text-gray-900">{tabCounts.fg}</span>
                        <span className="text-xs text-emerald-600 font-medium">FG Pending</span>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <span className="block text-xl font-bold text-gray-900">{tabCounts.all}</span>
                        <span className="text-xs text-gray-500 font-medium">Total</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── QC Inspection Slide-Over Panel ────────────────────────────────────── */}
      {/* Backdrop */}
      {panelOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity"
          onClick={closePanel}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[640px] max-w-[90vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          panelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {selectedOrder && currentTemplate && (
          <div className="h-full flex flex-col">
            {/* Panel Header */}
            <div className="px-6 py-5 border-b border-[var(--line)] bg-[var(--ivory-1)] shrink-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-md bg-[var(--saffron-50)] text-[var(--saffron-600)] border border-[var(--saffron-100)]">
                    {getQCType(selectedOrder.currentProcess)}
                  </span>
                  <span className="text-sm font-medium text-[var(--ink-2)]">
                    {selectedOrder.orderNumber}
                  </span>
                </div>
                <button
                  onClick={closePanel}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-[var(--ivory-2)] text-[var(--ink-2)] hover:bg-[var(--ivory-3)] transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <h2 className="font-serif text-2xl text-[var(--ink-0)] leading-tight">{currentTemplate.name}</h2>
              <p className="text-sm text-[var(--ink-2)] mt-1">{selectedOrder.productName}</p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-8">
                {/* ── Section 1: Checklist Items ──────────────────────────────── */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <ClipboardCheck size={18} className="text-blue-600" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Checklist Items
                    </h3>
                    <span className="text-xs text-gray-400 ml-auto">
                      {Object.values(checklistState).filter((c) => c.passed !== null).length} / {currentTemplate.checklist.length}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {currentTemplate.checklist.map((item) => {
                      const state = checklistState[item.id];
                      const isPassed = state?.passed === true;
                      const isFailed = state?.passed === false;
                      const isNotesExpanded = expandedNotes.has(item.id);

                      let rowBg = 'bg-white border-gray-200';
                      if (isPassed) rowBg = 'bg-emerald-50 border-emerald-200';
                      if (isFailed) rowBg = 'bg-red-50 border-red-200';

                      return (
                        <div key={item.id} className={`rounded-lg border ${rowBg} transition-all duration-200`}>
                          <div className="flex items-center gap-3 px-4 py-3">
                            {/* Pass/Fail Buttons */}
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => toggleChecklist(item.id, true)}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                  isPassed
                                    ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-emerald-100 hover:text-emerald-600'
                                }`}
                              >
                                <CheckCircle2 size={16} />
                              </button>
                              <button
                                onClick={() => toggleChecklist(item.id, false)}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                  isFailed
                                    ? 'bg-red-500 text-white shadow-sm shadow-red-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600'
                                }`}
                              >
                                <XCircle size={16} />
                              </button>
                            </div>

                            {/* Item Name */}
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm ${isPassed ? 'text-emerald-800' : isFailed ? 'text-red-800' : 'text-gray-800'}`}>
                                {item.name}
                                {item.required && <span className="text-red-500 ml-1">*</span>}
                              </span>
                            </div>

                            {/* Notes Toggle */}
                            <button
                              onClick={() => toggleNotes(item.id)}
                              className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                                isNotesExpanded || state?.notes
                                  ? 'bg-blue-100 text-blue-600'
                                  : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                              }`}
                            >
                              <FileText size={13} />
                            </button>
                          </div>

                          {/* Notes Field */}
                          {isNotesExpanded && (
                            <div className="px-4 pb-3">
                              <textarea
                                value={state?.notes || ''}
                                onChange={(e) => updateChecklistNotes(item.id, e.target.value)}
                                placeholder="Add notes..."
                                rows={2}
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none placeholder:text-gray-400"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* ── Section 2: QC Parameters ────────────────────────────────── */}
                {currentTemplate.parameters.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 size={18} className="text-blue-600" />
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                        Measured Parameters
                      </h3>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                              Parameter
                            </th>
                            <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 w-16">
                              Unit
                            </th>
                            <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 w-16">
                              Min
                            </th>
                            <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 w-16">
                              Max
                            </th>
                            <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 w-28">
                              Actual
                            </th>
                            <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 w-12">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentTemplate.parameters.map((param, idx) => {
                            const state = parameterState[param.id];
                            const validation = state?.value
                              ? validateParameter(state.value, param.min, param.max)
                              : 'empty';

                            return (
                              <tr
                                key={param.id}
                                className={`border-b border-gray-100 last:border-b-0 ${
                                  validation === 'in-range' ? 'bg-emerald-50/40' :
                                  validation === 'near-boundary' ? 'bg-amber-50/40' :
                                  validation === 'out-of-range' ? 'bg-red-50/40' :
                                  idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                    {param.critical && (
                                      <Zap size={13} className="text-amber-500 shrink-0" />
                                    )}
                                    {param.name}
                                  </span>
                                </td>
                                <td className="text-center px-3 py-3">
                                  <span className="text-xs text-gray-500 font-mono">{param.unit}</span>
                                </td>
                                <td className="text-center px-3 py-3">
                                  <span className="text-xs text-gray-600 font-mono">{param.min}</span>
                                </td>
                                <td className="text-center px-3 py-3">
                                  <span className="text-xs text-gray-600 font-mono">{param.max}</span>
                                </td>
                                <td className="text-center px-3 py-3">
                                  <input
                                    type="number"
                                    step="any"
                                    value={state?.value || ''}
                                    onChange={(e) => updateParameter(param.id, e.target.value)}
                                    placeholder="--"
                                    className={`w-24 text-center text-sm font-mono border rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${getValidationClasses(validation)}`}
                                  />
                                </td>
                                <td className="text-center px-3 py-3">
                                  {getValidationIcon(validation)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Zap size={11} className="text-amber-500" /> Critical parameter</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> In range</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Near boundary</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Out of range</span>
                    </div>
                  </section>
                )}

                {/* ── Section 3: Photos/Evidence ──────────────────────────────── */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Camera size={18} className="text-blue-600" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Photos / Evidence
                    </h3>
                    <span className="text-xs text-gray-400">(Optional)</span>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />

                  {uploadedPhotos.length > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-3">
                        {uploadedPhotos.map((photo, i) => (
                          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                            <img src={photo} alt={`Evidence ${i + 1}`} className="w-full h-full object-cover" />
                            <button
                              onClick={() => removePhoto(i)}
                              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-600 hover:border-blue-400 transition-colors"
                        >
                          <Upload size={18} />
                          <span className="text-xs">Add</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center gap-2 text-gray-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                    >
                      <Upload size={24} />
                      <span className="text-sm font-medium">Upload photos or evidence</span>
                      <span className="text-xs">Click to browse or drag & drop</span>
                    </button>
                  )}
                </section>

                {/* ── Section 4: Result Actions ───────────────────────────────── */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <ShieldCheck size={18} className="text-blue-600" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Inspection Result
                    </h3>
                  </div>

                  {/* Validation status */}
                  {!canPass && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm space-y-1">
                      {!allRequiredChecked && (
                        <div className="flex items-center gap-2 text-amber-800">
                          <AlertTriangle size={14} className="shrink-0" />
                          <span>All required checklist items must pass before marking as passed</span>
                        </div>
                      )}
                      {!noCriticalFails && (
                        <div className="flex items-center gap-2 text-amber-800">
                          <Zap size={14} className="shrink-0" />
                          <span>All critical parameters must be within acceptable range</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowPassConfirm(true)}
                      disabled={!canPass || submitting}
                      className={`flex-1 flex items-center justify-center gap-2 py-4 text-base font-bold rounded-xl transition-all ${
                        canPass
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-lg shadow-emerald-200'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <CheckCircle2 size={20} />
                      Pass
                    </button>
                    <button
                      onClick={() => { setShowFailModal('rework'); setFailReason(''); setFailNotes(''); }}
                      disabled={submitting}
                      className="flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold text-amber-700 bg-amber-50 border-2 border-amber-300 rounded-xl hover:bg-amber-100 active:bg-amber-200 transition-all"
                    >
                      <RotateCcw size={16} />
                      Rework
                    </button>
                    <button
                      onClick={() => { setShowFailModal('reject'); setFailReason(''); setFailNotes(''); }}
                      disabled={submitting}
                      className="flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold text-red-700 bg-red-50 border-2 border-red-300 rounded-xl hover:bg-red-100 active:bg-red-200 transition-all"
                    >
                      <Trash2 size={16} />
                      Reject
                    </button>
                  </div>
                </section>
              </div>
            </div>

            {/* ── Pass Confirmation Dialog ──────────────────────────────────── */}
            {showPassConfirm && (
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-10 p-6">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                  <div className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                      <CheckCircle2 size={32} className="text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm QC Pass</h3>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-semibold">{currentTemplate.name}</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-1">
                      {selectedOrder.orderNumber} - {selectedOrder.productName}
                    </p>
                    <p className="text-xs text-gray-400 mt-3">
                      This will advance the order to the next production stage.
                    </p>
                  </div>
                  <div className="flex border-t border-gray-200">
                    <button
                      onClick={() => setShowPassConfirm(false)}
                      disabled={submitting}
                      className="flex-1 py-4 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePass}
                      disabled={submitting}
                      className="flex-1 py-4 text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
                    >
                      {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      {submitting ? 'Processing...' : 'Confirm Pass'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Fail Modal ───────────────────────────────────────────────── */}
            {showFailModal && (
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-10 p-6">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        showFailModal === 'rework' ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        {showFailModal === 'rework'
                          ? <RotateCcw size={20} className="text-amber-600" />
                          : <Trash2 size={20} className="text-red-600" />
                        }
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {showFailModal === 'rework' ? 'Send to Rework' : 'Reject & Remove'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {showFailModal === 'rework'
                            ? 'Item will be sent back to an earlier stage for correction'
                            : 'Item will be removed from pipeline and BOM reversal triggered'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={failReason}
                        onChange={(e) => setFailReason(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        <option value="">Select reason...</option>
                        {REJECTION_REASONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Additional Notes
                      </label>
                      <textarea
                        value={failNotes}
                        onChange={(e) => setFailNotes(e.target.value)}
                        placeholder="Describe the issue in detail..."
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <div className="flex border-t border-gray-200">
                    <button
                      onClick={() => setShowFailModal(null)}
                      disabled={submitting}
                      className="flex-1 py-4 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleFail(showFailModal)}
                      disabled={!failReason || submitting}
                      className={`flex-1 py-4 text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                        failReason
                          ? showFailModal === 'rework'
                            ? 'text-amber-700 hover:bg-amber-50'
                            : 'text-red-700 hover:bg-red-50'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {submitting ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : showFailModal === 'rework' ? (
                        <RotateCcw size={16} />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      {submitting ? 'Processing...' : showFailModal === 'rework' ? 'Send to Rework' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat Card Component ───────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'amber' | 'green' | 'red' | 'blue';
}) {
  const colorMap = {
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600',
      valueText: 'text-amber-700',
    },
    green: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconText: 'text-emerald-600',
      valueText: 'text-emerald-700',
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconBg: 'bg-red-100',
      iconText: 'text-red-600',
      valueText: 'text-red-700',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
      valueText: 'text-blue-700',
    },
  };

  const c = colorMap[color];

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4 transition-all hover:shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${c.iconBg} flex items-center justify-center ${c.iconText}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold ${c.valueText} leading-tight mt-0.5`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

export default QCManagementPage;
